import { useMemo } from 'react';

import type { DataFlow } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { ScaledBytes, ScaledNumber } from '@packages/components/scaled';
import { type GridColDef } from '@packages/fenrir/components';

import type { FlowDataType } from '../../flow-data-type';
import { useFlowDataTypeParam } from '../../hooks/use-flow-data-type-param/use-flow-data-type-param';
import { Pivot } from '../pivot/pivot';
import Sparkline from '../sparkline/sparkline-chart/sparkline-chart';

import { SensorNameCell } from './sensor-name-cell/sensor-name-cell';

enum FlowDataColumn {
  Device = 'device',
  Protocol = 'protocol',
  Location = 'location',
  Site = 'site',
  Sensor = 'sensor',
  Devices = 'devices',
  Flows = 'flows',
  BytesIn = 'bytes-in',
  BytesOut = 'bytes-out',
  TotalBytes = 'total-bytes',
  BitsPerSecond = 'bits-per-second',
  Actions = 'actions',
}

const tabToColumns: Record<FlowDataType, FlowDataColumn[]> = {
  device: [
    FlowDataColumn.Device,
    FlowDataColumn.Flows,
    FlowDataColumn.BytesIn,
    FlowDataColumn.BytesOut,
    FlowDataColumn.TotalBytes,
    FlowDataColumn.BitsPerSecond,
    FlowDataColumn.Actions,
  ],
  protocol: [
    FlowDataColumn.Protocol,
    FlowDataColumn.Devices,
    FlowDataColumn.Flows,
    FlowDataColumn.BytesIn,
    FlowDataColumn.BytesOut,
    FlowDataColumn.TotalBytes,
    FlowDataColumn.BitsPerSecond,
    FlowDataColumn.Actions,
  ],
  location: [
    FlowDataColumn.Location,
    FlowDataColumn.Devices,
    FlowDataColumn.Flows,
    FlowDataColumn.BytesIn,
    FlowDataColumn.BytesOut,
    FlowDataColumn.TotalBytes,
    FlowDataColumn.BitsPerSecond,
    FlowDataColumn.Actions,
  ],
  site: [
    FlowDataColumn.Site,
    FlowDataColumn.Devices,
    FlowDataColumn.Flows,
    FlowDataColumn.BytesIn,
    FlowDataColumn.BytesOut,
    FlowDataColumn.TotalBytes,
    FlowDataColumn.BitsPerSecond,
    FlowDataColumn.Actions,
  ],
  sensor: [
    FlowDataColumn.Sensor,
    FlowDataColumn.Devices,
    FlowDataColumn.Flows,
    FlowDataColumn.BytesIn,
    FlowDataColumn.BytesOut,
    FlowDataColumn.TotalBytes,
    FlowDataColumn.BitsPerSecond,
    FlowDataColumn.Actions,
  ],
};

export const useFlowDataColumns = () => {
  const { t } = useTranslation('flow');

  const flowDataType = useFlowDataTypeParam();

  const columnDefinitions: Record<
    FlowDataColumn,
    GridColDef<DataFlow>
  > = useMemo(
    () => ({
      [FlowDataColumn.Device]: {
        field: 'name',
        headerName: t(`table.columns.${FlowDataColumn.Device}`),
        flex: 0.2,
        minWidth: 150,
      },
      [FlowDataColumn.Protocol]: {
        field: 'name',
        headerName: t(`table.columns.${FlowDataColumn.Protocol}`),
        flex: 0.2,
        minWidth: 150,
      },
      [FlowDataColumn.Location]: {
        field: 'name',
        headerName: t(`table.columns.${FlowDataColumn.Location}`),
        flex: 0.2,
        minWidth: 150,
      },
      [FlowDataColumn.Site]: {
        field: 'name',
        headerName: t(`table.columns.${FlowDataColumn.Site}`),
        flex: 0.2,
        minWidth: 150,
      },
      [FlowDataColumn.Sensor]: {
        field: 'name',
        headerName: t(`table.columns.${FlowDataColumn.Sensor}`),
        renderCell: (value) => <SensorNameCell deploymentId={value.row.name} />,
        sortable: false,
        flex: 0.2,
        minWidth: 150,
      },
      [FlowDataColumn.Devices]: {
        field: 'devices',
        type: 'number',
        headerName: t(`table.columns.${FlowDataColumn.Devices}`),
        flex: 0.2,
        minWidth: 85,
        renderCell: ({ row }) => <ScaledNumber number={row?.devices || 0} />,
      },
      [FlowDataColumn.Flows]: {
        field: 'flows',
        type: 'number',
        headerName: t(`table.columns.${FlowDataColumn.Flows}`),
        flex: 0.2,
        minWidth: 85,
        renderCell: ({ row }) => <ScaledNumber number={row.flows} />,
      },
      [FlowDataColumn.BytesIn]: {
        field: 'bytes_in',
        type: 'number',
        headerName: t(`table.columns.${FlowDataColumn.BytesIn}`),
        flex: 0.2,
        minWidth: 100,
        renderCell: ({ row }) => (
          <ScaledBytes bytes={row.bytesIn} decimals={1} />
        ),
      },
      [FlowDataColumn.BytesOut]: {
        field: 'bytes_out',
        type: 'number',
        headerName: t(`table.columns.${FlowDataColumn.BytesOut}`),
        flex: 0.2,
        minWidth: 110,
        renderCell: ({ row }) => (
          <ScaledBytes bytes={row.bytesOut} decimals={1} />
        ),
      },
      [FlowDataColumn.TotalBytes]: {
        field: 'bytes',
        type: 'number',
        headerName: t(`table.columns.${FlowDataColumn.TotalBytes}`),
        flex: 0.2,
        minWidth: 100,
        renderCell: ({ row }) => <ScaledBytes bytes={row.bytes} decimals={1} />,
      },
      [FlowDataColumn.BitsPerSecond]: {
        field: 'bps',
        type: 'custom',
        sortable: false,
        headerName: t(`table.columns.${FlowDataColumn.BitsPerSecond}`),
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => <Sparkline data={row.bps || []} />,
      },
      [FlowDataColumn.Actions]: {
        field: 'actions',
        headerName: '',
        type: 'custom',
        hideable: false,
        sortable: false,
        resizable: false,
        align: 'right',
        width: 85,
        renderCell: ({ row }) => {
          return <Pivot data={row} />;
        },
      },
    }),
    [t],
  );

  const flowDataColumns = tabToColumns[flowDataType];

  return useMemo(
    () =>
      flowDataColumns.map(
        (flowDataColumn) => columnDefinitions[flowDataColumn],
      ),
    [columnDefinitions, flowDataColumns],
  );
};
